import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";

import {
  Box,
  CircularProgress,
  FormControl,
  makeStyles,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";

import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import ShowTicketOpen from "../ShowTicketOpenModal";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import StandardModal from "../ui/StandardModal";
import { Inbox, Kanban, Sparkles, Tag as TagIcon, UserRound } from "lucide-react";
import { DEFAULT_ACCEPT_MODAL_CONFIG, resolveAcceptModalConfig } from "../../utils/ticketLayoutConfig";

const useStyles = makeStyles((theme) => ({
  autoComplete: {
    width: 300,
  },
  maxWidth: {
    width: "100%",
  },
  buttonColorError: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
  },
  formControl: {
    margin: 0,
    minWidth: "100%",
  },
  intro: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    borderRadius: 16,
    background: "linear-gradient(135deg, rgba(37, 99, 235, 0.1), rgba(14, 165, 233, 0.05))",
    border: "1px solid rgba(37, 99, 235, 0.14)",
  },
  introIcon: {
    width: 42,
    height: 42,
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#2563eb",
    background: "rgba(255, 255, 255, 0.8)",
    borderRadius: 13,
    boxShadow: "0 5px 14px rgba(37, 99, 235, 0.12)",
  },
  introTitle: {
    fontWeight: 800,
    color: theme.palette.text.primary,
    fontSize: "0.98rem",
    lineHeight: 1.25,
  },
  introText: {
    marginTop: 3,
    color: theme.palette.text.secondary,
    fontSize: "0.78rem",
    lineHeight: 1.4,
  },
  fields: {
    display: "grid",
    gap: theme.spacing(1.25),
  },
  fieldCard: {
    padding: theme.spacing(1.25),
    borderRadius: 14,
    border: "1px solid rgba(148, 163, 184, 0.24)",
    background: theme.palette.type === "dark" ? "rgba(30, 41, 59, 0.46)" : "rgba(248, 250, 252, 0.82)",
    transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease",
    "&:focus-within": {
      borderColor: "rgba(37, 99, 235, 0.55)",
      boxShadow: "0 5px 18px rgba(37, 99, 235, 0.1)",
      transform: "translateY(-1px)",
    },
  },
  fieldHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(0.75),
  },
  fieldIcon: {
    width: 17,
    height: 17,
    color: theme.palette.primary.main,
  },
  fieldLabel: {
    fontSize: "0.78rem",
    fontWeight: 800,
    color: theme.palette.text.primary,
  },
  fieldHint: {
    marginLeft: "auto",
    fontSize: "0.68rem",
    color: theme.palette.text.secondary,
  },
  loadingState: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: 12,
    color: theme.palette.text.secondary,
    background: theme.palette.action.hover,
    fontSize: "0.8rem",
  },
  selectMenu: {
    height: 40,
    minHeight: 40,
    borderRadius: 12,
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: 12,
    },
    "&.MuiOutlinedInput-root": {
      fontSize: "0.9rem",
      "& fieldset": {
        borderColor: theme.palette.divider,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.light,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  },
  menuItem: {
    fontSize: "0.9rem",
    height: 36,
    minHeight: 36,
    paddingTop: 4,
    paddingBottom: 4,
  },
  inputLabel: {
    fontSize: "0.9rem",
    transform: "translate(14px, 12px) scale(1)",
    "&.MuiInputLabel-shrink": {
      transform: "translate(14px, -6px) scale(0.75)",
    },
  },
}));

const AcceptTicketWithouSelectQueue = ({
  modalOpen,
  onClose,
  ticketId,
  ticket,
  initialQueueId = "",
  showQueue = true,
}) => {
  const history = useHistory();
  const classes = useStyles();
  const [selectedQueue, setSelectedQueue] = useState(initialQueueId || "");
  const [selectedTag, setSelectedTag] = useState("");
  const [selectedStage, setSelectedStage] = useState("");

  const resolveInitialContactType = useCallback(() => {
    const rawType = ticket?.contact?.clientType;
    if (rawType) {
      const lower = String(rawType)
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      if (lower === "funcionario") return "Funcionario";
      if (lower === "fornecedor") return "Fornecedor";
      if (lower === "cliente") return "Cliente";
      if (lower === "lead") return "Lead";
      return rawType;
    }
    const search = history?.location?.search || window?.location?.hash || "";
    if (search.includes("tab=employees")) return "Funcionario";
    if (search.includes("tab=suppliers")) return "Fornecedor";
    return "Lead";
  }, [ticket?.contact?.clientType, history?.location?.search]);

  const [selectedContactType, setSelectedContactType] = useState(
    resolveInitialContactType()
  );
  const [modalConfig, setModalConfig] = useState(DEFAULT_ACCEPT_MODAL_CONFIG);
  const [tags, setTags] = useState([]);
  const [kanbanStages, setKanbanStages] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useContext(AuthContext);
  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");
  const { setTabOpen } = useContext(TicketsContext);
  const isMounted = useRef(true);

  const { get: getSetting } = useCompanySettings();

  const normalizedClientType = String(selectedContactType || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  const isSharedContact =
    normalizedClientType === "funcionario" ||
    normalizedClientType === "fornecedor";

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    try {
      if (user?.queues?.length === 1) {
        setSelectedQueue(user.queues[0].id);
      }
    } catch (err) {
      if (isMounted.current) {
        setLoading(false);
        toastError(err);
      }
    }
  }, [selectedQueue, user?.queues]);

  useEffect(() => {
    if (!modalOpen) return;
    setSelectedQueue(initialQueueId || (ticket?.queueId ?? ""));
    setSelectedContactType(resolveInitialContactType());
    setSelectedTag("");
    setSelectedStage("");

    const loadOptions = async () => {
      setOptionsLoading(true);
      try {
        let loadedConfig = { ...DEFAULT_ACCEPT_MODAL_CONFIG };
        try {
          const setting = await getSetting({ column: "ticketAcceptModalConfig" });
          const raw = setting?.ticketAcceptModalConfig || setting?.value;
          if (raw) {
            loadedConfig = resolveAcceptModalConfig(raw);
          } else {
            const layoutSetting = await getSetting({ column: "ticketLayoutConfig" });
            const layoutRaw = layoutSetting?.ticketLayoutConfig || layoutSetting?.value;
            if (layoutRaw) {
              loadedConfig = resolveAcceptModalConfig(layoutRaw);
            }
          }
        } catch (_cfgErr) {
          // ignore fallback
        }

        if (isMounted.current) {
          setModalConfig(loadedConfig);
        }

        const promises = [];
        if (loadedConfig.enabled && loadedConfig.showTag) {
          promises.push(api.get("/tags/list", { params: { kanban: 0 } }));
        } else {
          promises.push(Promise.resolve({ data: [] }));
        }

        if (loadedConfig.enabled && loadedConfig.showKanban) {
          promises.push(api.get("/tag/kanban"));
        } else {
          promises.push(Promise.resolve({ data: { lista: [] } }));
        }

        const [{ data: regularTags }, { data: stages }] = await Promise.all(promises);
        if (isMounted.current) {
          setTags(Array.isArray(regularTags) ? regularTags : []);
          setKanbanStages(Array.isArray(stages?.lista) ? stages.lista : []);
        }
      } catch (err) {
        if (isMounted.current) toastError(err);
      } finally {
        if (isMounted.current) setOptionsLoading(false);
      }
    };

    loadOptions();
  }, [modalOpen, initialQueueId, ticket?.queueId, resolveInitialContactType, getSetting]);

  const handleClose = () => {
    if (!isMounted.current) return;
    onClose();
    setSelectedQueue("");
  };

  const handleCloseAlert = () => {
    if (!isMounted.current) return;
    setOpenAlert(false);
    setLoading(false);
  };

  const handleSendMessage = async (id) => {
    if (!isMounted.current) return;

    let isGreetingMessage = false;

    try {
      const setting = await getSetting({
        column: "sendGreetingAccepted",
      });
      if (setting.sendGreetingAccepted === "enabled") isGreetingMessage = true;
    } catch (err) {
      if (isMounted.current) toastError(err);
      return;
    }

    if (!isGreetingMessage) return;

    let settingMessage;
    try {
      settingMessage = await getSetting({
        column: "greetingAcceptedMessage",
      });
    } catch (err) {
      if (isMounted.current) toastError(err);
      return;
    }

    if (
      !settingMessage ||
      !settingMessage.greetingAcceptedMessage ||
      settingMessage.greetingAcceptedMessage.trim() === ""
    ) {
      if (isMounted.current) {
        toastError("Mensagem de saudação não configurada");
      }
      return;
    }

    if (
      (!ticket.isGroup || ticket.whatsapp?.groupAsTicket === "enabled") &&
      ticket.status === "pending"
    ) {
      let msg = settingMessage.greetingAcceptedMessage;

      if (msg.trim() === "") {
        return;
      }
      const message = {
        read: 1,
        fromMe: true,
        mediaUrl: "",
        body: `${msg.trim()}`,
      };
      try {
        await api.post(`/messages/${id}`, message);
      } catch (err) {
        if (isMounted.current) toastError(err);
      }
    }
  };

  const handleUpdateTicketStatus = async (queueId) => {
    if (!isMounted.current) return;
    if (
      (!queueId && showQueue && !isSharedContact) ||
      (modalConfig.enabled && modalConfig.showTag && !selectedTag) ||
      (modalConfig.enabled && modalConfig.showKanban && !selectedStage) ||
      (modalConfig.enabled && modalConfig.showContactType && !selectedContactType)
    ) {
      return;
    }
    setLoading(true);

    try {
      if (modalConfig.enabled && modalConfig.showContactType && selectedContactType && ticket?.contact?.id) {
        await api.put(`/contacts/${ticket.contact.id}`, {
          clientType: selectedContactType,
        });
      }

      const otherTicket = await api.put(`/tickets/${ticketId}`, {
        status:
          ticket.isGroup && ticket.channel === "whatsapp" ? "group" : "open",
        userId: isSharedContact ? null : user?.id || null,
        queueId: isSharedContact ? null : queueId || null,
      });

      // Regular tag is synced to contact tags, NOT to /ticket-tags/:ticketId/:tagId
      if (modalConfig.enabled && modalConfig.showTag && selectedTag && ticket?.contact?.id) {
        try {
          const currentTags = Array.isArray(ticket?.contact?.tags)
            ? ticket.contact.tags.map((t) => ({ id: t.id }))
            : [];
          const alreadyHas = currentTags.some(
            (t) => Number(t.id) === Number(selectedTag)
          );
          const newTags = alreadyHas
            ? currentTags
            : [...currentTags, { id: Number(selectedTag) }];
          await api.post("/tags/sync", {
            contactId: ticket.contact.id,
            tags: newTags,
          });
        } catch (tagErr) {
          console.error("Erro ao sincronizar tag do contato:", tagErr);
        }
      }

      // Kanban stage is updated via /ticket-tags/:ticketId/:selectedStage
      if (modalConfig.enabled && modalConfig.showKanban && selectedStage) {
        await api.put(`/ticket-tags/${ticketId}/${selectedStage}`);
      }

      const sharedTab =
        normalizedClientType === "funcionario"
          ? "employees"
          : normalizedClientType === "fornecedor"
            ? "suppliers"
            : "";

      const navigateToTicket = `/tickets/${
        otherTicket.data.uuid || ticket.uuid
      }${sharedTab ? `?tab=${sharedTab}` : ""}`;
      const newTabValue =
        sharedTab || (otherTicket.data.isGroup || ticket.isGroup ? "group" : "open");

      handleClose();

      if (otherTicket.data.id !== ticket.id) {
        if (otherTicket.data.userId !== user?.id && !isSharedContact) {
          if (isMounted.current) {
            setOpenAlert(true);
            setUserTicketOpen(otherTicket.data.user.name);
            setQueueTicketOpen(otherTicket.data.queue.name);
            setLoading(false);
          }
        } else {
          if (isMounted.current) {
            setLoading(false);
          }
          setTabOpen(newTabValue);
          history.push(navigateToTicket);
        }
      } else {
        await handleSendMessage(ticket.id);
        if (isMounted.current) {
          setLoading(false);
        }
        setTabOpen(newTabValue);
        history.push(navigateToTicket);
      }
    } catch (err) {
      if (isMounted.current) {
        setLoading(false);
        toastError(err);
      }
    }
  };

  return (
    <>
      <StandardModal
        open={modalOpen}
        onClose={handleClose}
        title={i18n.t("ticketsList.acceptModal.title")}
        maxWidth="sm"
        titleIcon={<Sparkles size={20} />}
        paperStyle={{ borderRadius: 22 }}
        cancelText={i18n.t("ticketsList.buttons.cancel")}
        confirmationText={i18n.t("ticketsList.buttons.start")}
        onConfirm={() => handleUpdateTicketStatus(selectedQueue)}
        disableConfirmButton={
          (!isSharedContact && showQueue && selectedQueue === "") ||
          (modalConfig.enabled && modalConfig.showTag && selectedTag === "") ||
          (modalConfig.enabled && modalConfig.showKanban && selectedStage === "") ||
          (modalConfig.enabled && modalConfig.showContactType && selectedContactType === "")
        }
        loading={loading}
        useButtonSpinner={true}
        disableBackdropClick={true}
        disableEscapeKeyDown={true}
      >
        <Box onClick={(e) => e.stopPropagation()}>
          <div className={classes.intro}>
            <div className={classes.introIcon}><Sparkles size={21} /></div>
            <div>
              <Typography className={classes.introTitle}>
                {modalConfig.enabled ? "Prepare este atendimento" : "Aceitar atendimento"}
              </Typography>
              <Typography className={classes.introText}>
                {modalConfig.enabled
                  ? "Defina a classificação inicial antes de começar a conversa."
                  : "Selecione a fila para iniciar este atendimento."}
              </Typography>
            </div>
          </div>

          {optionsLoading ? (
            <div className={classes.loadingState}>
              <CircularProgress size={18} color="primary" />
              <span>Carregando opções de atendimento...</span>
            </div>
          ) : (
            <div className={classes.fields}>
              {!isSharedContact && showQueue && (
                <div className={classes.fieldCard}>
                  <div className={classes.fieldHeader}><Inbox className={classes.fieldIcon} /><span className={classes.fieldLabel}>{i18n.t("ticketsList.acceptModal.queue")}</span><span className={classes.fieldHint}>Obrigatório</span></div>
                  <FormControl variant="outlined" className={classes.formControl}>
                    <Select value={selectedQueue} onChange={(e) => setSelectedQueue(e.target.value)} className={classes.selectMenu} displayEmpty>
                      <MenuItem value="" className={classes.menuItem}>Escolha uma fila</MenuItem>
                      {user?.queues?.map((queue) => <MenuItem key={queue.id} value={queue.id} className={classes.menuItem}>{queue.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </div>
              )}

              {modalConfig.enabled && modalConfig.showTag && (
                <div className={classes.fieldCard}>
                  <div className={classes.fieldHeader}><TagIcon className={classes.fieldIcon} /><span className={classes.fieldLabel}>Tag</span><span className={classes.fieldHint}>Obrigatório</span></div>
                  <FormControl variant="outlined" className={classes.formControl}>
                    <Select value={selectedTag} onChange={(e) => setSelectedTag(e.target.value)} className={classes.selectMenu} displayEmpty>
                      <MenuItem value="" className={classes.menuItem}>Escolha uma tag</MenuItem>
                      {tags.map((tag) => <MenuItem key={tag.id} value={tag.id} className={classes.menuItem}>{tag.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </div>
              )}

              {modalConfig.enabled && modalConfig.showKanban && (
                <div className={classes.fieldCard}>
                  <div className={classes.fieldHeader}><Kanban className={classes.fieldIcon} /><span className={classes.fieldLabel}>Etapa do Kanban</span><span className={classes.fieldHint}>Obrigatório</span></div>
                  <FormControl variant="outlined" className={classes.formControl}>
                    <Select value={selectedStage} onChange={(e) => setSelectedStage(e.target.value)} className={classes.selectMenu} displayEmpty>
                      <MenuItem value="" className={classes.menuItem}>Escolha uma etapa</MenuItem>
                      {kanbanStages.map((stage) => <MenuItem key={stage.id} value={stage.id} className={classes.menuItem}>{stage.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </div>
              )}

              {modalConfig.enabled && modalConfig.showContactType && (
                <div className={classes.fieldCard}>
                  <div className={classes.fieldHeader}><UserRound className={classes.fieldIcon} /><span className={classes.fieldLabel}>Tipo de contato</span><span className={classes.fieldHint}>Obrigatório</span></div>
                  <FormControl variant="outlined" className={classes.formControl}>
                    <Select value={selectedContactType} onChange={(e) => setSelectedContactType(e.target.value)} className={classes.selectMenu} displayEmpty>
                      <MenuItem value="" className={classes.menuItem}>Escolha o tipo</MenuItem>
                      <MenuItem value="Lead" className={classes.menuItem}>Lead</MenuItem>
                      <MenuItem value="Cliente" className={classes.menuItem}>Cliente</MenuItem>
                      <MenuItem value="Funcionario" className={classes.menuItem}>Funcionário</MenuItem>
                      <MenuItem value="Fornecedor" className={classes.menuItem}>Fornecedor</MenuItem>
                    </Select>
                  </FormControl>
                </div>
              )}
            </div>
          )}
        </Box>
      </StandardModal>
      <ShowTicketOpen
        isOpen={openAlert}
        handleClose={handleCloseAlert}
        user={userTicketOpen}
        queue={queueTicketOpen}
      />
    </>
  );
};

export default AcceptTicketWithouSelectQueue;
